import { skipToken } from "@reduxjs/toolkit/query";
import { AvatarSize } from "marketplace/_views/avatars/avatar-size";
import { useGetIndividualAvatarQuery, useGetIndividualByIdQuery } from "marketplace/individuals/_api/individual-api";
import type { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import { useSession } from "users/__session/session-context";
import useSessionUser from "permissions/session/use-session-user";

export type OwnIndividual = {
  /** The signed-in person's marketplace individual, once read. */
  individual?: IndividualJSON;
  /** Their photo as a data URL, or undefined when they have none or it has not arrived yet. */
  photo?: string;
  isLoading: boolean;
  /** The profile or the photo could not be read. Nothing is known about either, not that they are empty. */
  isError: boolean;
  retry: () => void;
};

/**
 * The signed-in person's own marketplace individual and photo.
 *
 * The photo lives on the marketplace profile, not on the account, so the header and Settings both
 * read it from there. A person without an individual — a session a response behind the profile the
 * API made for it — and a person in restricted onboarding, who may not read the marketplace at
 * all, ask for nothing and get initials.
 */
export function useOwnIndividual(size: AvatarSize = "small"): OwnIndividual {
  const session = useSession();
  const user = useSessionUser();
  // The header stays mounted when marketplace access is revoked. Stop its profile and photo
  // requests as soon as the current grants change, and discard any previously cached profile.
  const individualId = session.isRestrictedOnboarding || !user?.canReadMarketplaceIndividual()
    ? undefined : user.individualId?.value;

  const individualQuery = useGetIndividualByIdQuery(individualId ? { individualId } : skipToken);
  const individual = individualId ? individualQuery.data : undefined;
  const hasPhoto = Boolean(individual?.avatarId);

  const photoQuery = useGetIndividualAvatarQuery(
    individualId && hasPhoto ? { individualId, size } : skipToken,
  );

  return {
    individual,
    // An empty answer is no photo, not a broken image.
    photo: hasPhoto ? photoQuery.data || undefined : undefined,
    isLoading: individualQuery.isLoading || photoQuery.isLoading,
    isError: Boolean(individualId) && (individualQuery.isError || (hasPhoto && photoQuery.isError)),
    retry: () => {
      if (!individualId) return;
      if (individualQuery.isError) void individualQuery.refetch();
      if (hasPhoto && photoQuery.isError) void photoQuery.refetch();
    },
  };
}
